import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AuthService, UsageService, AppService, InstitutionService } from '../../shared/services'
import { isWithinInterval, parseISO, format } from 'date-fns';
import { combineLatest } from 'rxjs';
import { switchMap } from 'rxjs/operators';

interface IPlanData{
  usagePlanId?: string;
  billingPeriod?: any;
  unitPrice?: number;
  unitsUsed?: number;
  units?: number;
  overagePrice?: number;
}

@Component({
  selector: 'summary',
  templateUrl: './summary.component.html',
  styleUrls: ['./summary.component.scss'],
})
export class SummaryComponent implements OnDestroy{

  private subscriptions$;
  public loading: boolean = true;
  public viewMore: boolean  = false;
  private dateF = 'yyyy-MM-dd';

  public planData:IPlanData = { };
  pageParams;
  tableData = [];
  tableIndex: number = 0;
  usagePlans = [];
  auth;
  public searchValue: string;
  public status: 'loading'|'save' = 'save';

  public paginateConfig = {
		itemsPerPage: 10,
		currentPage: 1,
    totalItems: null
	};


  constructor(
    private route: ActivatedRoute,
    private appService: AppService,
    private usageService: UsageService,
    private authService: AuthService,
    private institution: InstitutionService
  ) {
    this.start();
  }

  reset(){
    this.loading = true;
    this.viewMore = false;
    this.query = {};
    this.usagePlans = [];
    this.planData = {};
    this.tableData = [];
    this.newInstitutionSetup();
  }

  private start(){
    this.subscriptions$ = combineLatest(
      this.authService.get(),
      this.route.params
    ).pipe(
      switchMap( ([ auth, params ]) => {
        this.authService.allowed();
        this.toggleOption = 'course';
        this.reset();
        this.pageParams = params;
        if(auth){
          this.auth = auth;
        }
        return this.institution.find(params.institutionId)
      }),
      switchMap( data => {
        let institution:any = data;

        this.appService.currentInstitution$.next(institution);
        this.pageParams = {
          ...this.pageParams,
          ...institution
        };
        return this.getTableData()
      }),
    ).subscribe( data => {
      this.header = this.getHeader();

      if(data && data.usagePlans){
        this.planData = this.findClosestDate( data.usagePlans );
        this.usagePlans = data.usagePlans;
      }else if(this.auth.type ==='internal'){
        this.newInstitutionSetup();
      }
      this.loading = false;
    });
  }

  private findClosestDate(plans){
    let data = plans.filter( plan => {
      let date = plan.billingPeriod.split(' - ');
      return isWithinInterval(new Date(), {
        start: parseISO(date[0]),
        end: parseISO(date[1])
      });
    });

    return data && data.length ? data[0] : plans[0];
  }

  private getHeader(){
    let result = ''
    let app = this.appService.find(this.pageParams.appType);

    if(app.length){
      result = app[0].name;
    }

    return result;
  }

  private newInstitutionSetup(){
    this.planData = {
      usagePlanId: null,
      units: 0,
      unitPrice: 0,
      unitsUsed: 0,
      overagePrice: 0,
      billingPeriod: `${format(new Date(), this.dateF)} - ${format(new Date(), this.dateF)}`,
    };
  }


  toggleDatePlans(query){
    this.viewMore = false;
    let startdate = format(new Date(query.startdate), this.dateF);
    let enddate = format(new Date(query.enddate), this.dateF);
    this.getPlan({startdate, enddate}, this.usagePlans);
  }

  public header;

  public getTableHeaders(){
    let headers = {
      'deptName': 'Department Name',
      'assessments': 'Assessments',
    };

    headers['deptName'] = this.toggleOption  === 'department' ? 'Department Name' : 'Course Name';
    headers['unitsUsed'] = this.pageParams.appType === 'exam-id' ? 'Unique Licenses' : 'Units Used';
    return Object.values( headers );
  }

  private query;

  private getTableData(data: any = {}){
    this.query = { ...this.query, ...data };

    if(!this.query.tab){
      this.query.tab = this.toggleOption;
    }

    return this.usageService.get(this.pageParams.appType, this.pageParams.institutionId, this.query).then( result => {
      this.loadingTable = false;
      if(result){
        this.tableData = result.summary || [];
        this.paginateConfig.totalItems = this.tableData.length
        this.usagePlans = [ ...result.usagePlans ];
        this.getPlan(this.query, result.usagePlans);
        return result;
      }else{
        this.reset();
        this.loading = false;
      }
    }).catch( error => {
      this.loadingTable = false;
      this.tableData = [];
      this.usagePlans = [];
      console.error('Table Data - Usage Service', error.message);
    });
  }

  private getPlan(query, usagePlans){
    if(query && query.startdate && query.enddate){
      let data = usagePlans.filter( plan => plan.billingPeriod === `${query.startdate} - ${query.enddate}`)[0];
      this.planData = data;
    }else{
      this.planData = usagePlans[0];
    }
  }

  public toggleOption = 'course';

  private extractDate(data: string){
    let date = data.split(' - ');
    return { startdate: date[0], enddate: date[1] }
  }

  loadingTable = false;
  public onViewMore(status){
    this.viewMore = status;
    if(status){
      let tab = this.toggleOption;
      let dates = this.extractDate( this.planData.billingPeriod);
      let data = { tab, ...dates };
      this.loadingTable = true;
      this.getTableData(data);
    }
  }

  public onToggleOptions(selected: 'department'|'course'){
    let tab = selected;
    this.toggleOption = selected;
    let dates = this.extractDate( this.planData.billingPeriod);
    let data = { tab, ...dates };
    this.loadingTable = true;
    this.getTableData(data);
  }


  /* onExport(){
   *   this.usageService.export(this.pageParams.appType, this.pageParams.institutionId, {}).then( data => {
   *     // window.location.assign(data);
   *   }).catch( error => {
   *     console.error('Table Data - Usage Service', error.message);
   *   });
   * } */

  public onSave(event){
    this.status = 'loading';

    let save;

    if(event.data){
      let data = { ...event.data };
      if(event.saveType === 'save'){
        save = this.usageService.post(this.pageParams.appType, data);
      }else if(event.saveType === 'update'){
        save = this.usageService.put(this.pageParams.appType, data);
      }else if(event.saveType === 'delete'){
        save = this.usageService.delete(this.pageParams.appType, data);
      }

      save.then(result => {
        this.status = 'save';
        if(this.auth.type ==='internal'){
          console.table('RESULT: ', { ...result })
          console.table('DATA: ', {...data })
        }
        this.cleanUpdate(result, data);
        this.getTableData();
      })
    }
  }

  private cleanUpdate(result, plan){
    if(result && result['usage_plan_id']){
      plan['usagePlanId'] = result['usage_plan_id'];
      plan['billingPeriod'] = `${plan.billingStartDate} - ${plan.billingEndDate}`;
      this.query.startdate  = plan.billingStartDate;
      this.query.enddate = plan.billingEndDate;
      this.planData = plan;
    }else{
      delete this.query.startdate;
      delete this.query.enddate;
    }
  }

  public onSearch(data){
    this.searchValue = data.detail ? data.detail: null;
  }

  selected(i){
    this.paginateConfig.currentPage = i;
  }

  ngOnDestroy(){
    this.subscriptions$.unsubscribe();
  }

}

